import * as XLSX from 'xlsx';

/**
 * Leitura da lista de seguidores exportada do Instagram (CSV ou planilha).
 * Cada ferramenta de exportação nomeia as colunas de um jeito; aqui se acha a
 * coluna pelo nome e a categoria pelo que o perfil diz de si (@, nome, bio).
 */

const COLUMN_PATTERNS = {
  username: /^(user_?name|usuario|usuário|login|handle|perfil|instagram|ig|account)$/,
  profileUrl: /(profile_?url|url|link)/,
  name: /^(full_?name|nome( completo)?|name)$/,
  bio: /(bio|biography|descri)/,
  email: /(e-?mail)/,
  phone: /(phone|telefone|whats|celular)/,
};

/** Ordem importa: o primeiro que casar define a categoria */
const CATEGORY_RULES = [
  ['physio', /fisio|crefito/],
  ['osteopathy', /osteopat/],
  ['pilates', /pilates/],
  ['nutrition', /nutri|\bcrn/],
  ['speech', /fono/],
  ['psychology', /psicolog|psicanal|\bpsi\b|\bcrp\b/],
  ['dentistry', /odonto|dentist|\bcro\b/],
  ['aesthetics', /estetic|harmoniza|biomedic|depila|laser|skin/],
  ['beauty', /salao|cabelei|beleza|beauty|nail|unha|manicure|sobrancelha|cilio|lash|make|maquia|hair/],
  ['therapies', /terap|acupunt|holistic|reiki|massote|quiropra|ayurv/],
  ['medicine', /medic|\bdra?\b|\bcrm\b|pediatr|dermato|ortoped|gineco|cardio/],
];

/**
 * @param {string} value
 * @returns {string} minúsculo e sem acento
 */
function plain(value) {
  return String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * @param {string} text @, nome e bio juntos
 * @returns {string|null} valor de CATEGORY_OPTIONS ou null quando não parece profissional
 */
export function guessCategory(text) {
  const value = plain(text);
  const rule = CATEGORY_RULES.find(([, pattern]) => pattern.test(value));
  return rule ? rule[0] : null;
}

/**
 * @param {string[]} headers
 * @returns {Object<string, string|undefined>} campo -> cabeçalho da planilha
 */
function mapColumns(headers) {
  const map = {};
  Object.entries(COLUMN_PATTERNS).forEach(([field, pattern]) => {
    map[field] = headers.find((header) => pattern.test(plain(header).trim()));
  });
  return map;
}

/**
 * "https://www.instagram.com/fulana/", "@fulana" -> "fulana".
 * @param {string} value
 * @returns {string}
 */
function usernameOf(value) {
  const text = String(value || '').trim();
  const fromUrl = text.match(/instagram\.com\/([A-Za-z0-9_.]+)/i);
  const handle = fromUrl ? fromUrl[1] : text.replace(/^@/, '');
  return /^[A-Za-z0-9_.]{1,30}$/.test(handle) ? handle : '';
}

/**
 * @param {File} file CSV, XLS ou XLSX
 * @returns {Promise<Array<{ username: string, name: string, bio: string, email: string, phone: string, category: string|null }>>}
 */
export async function readFollowersFile(file) {
  const buffer = await file.arrayBuffer();
  // CSV sem BOM o XLSX lê como Latin-1 e estraga os acentos; decodifica como UTF-8 antes
  const workbook = /\.csv$/i.test(file.name || '')
    ? XLSX.read(new TextDecoder('utf-8').decode(buffer), { type: 'string' })
    : XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
  if (rows.length === 0) return [];

  const columns = mapColumns(Object.keys(rows[0]));
  if (!columns.username && !columns.profileUrl) {
    throw new Error('Não achei a coluna com o @ (username) nem o link do perfil.');
  }

  const seen = new Set();
  return rows.reduce((list, row) => {
    const username = usernameOf(row[columns.username] || row[columns.profileUrl]);
    const key = username.toLowerCase();
    if (!username || seen.has(key)) return list;
    seen.add(key);

    const name = String(row[columns.name] || '').trim();
    const bio = String(row[columns.bio] || '').trim();
    list.push({
      username,
      name,
      bio,
      email: String(row[columns.email] || '').trim(),
      phone: String(row[columns.phone] || '').trim(),
      category: guessCategory(`${username} ${name} ${bio}`),
    });
    return list;
  }, []);
}
